import { Fetcher, dig } from '@this/src/util';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { RadioGroup, Radio } from '@this/components/shared/ui/inputs/radio';
import RailwayArea, {
  type RailwayAreaResponseArgs,
  convertRailwayAreaResponseToArgs
} from '@this/domain/railway_area';

const RailwayAreas = () => {
  const [railwayAreasLoading, setRailwayAreasLoading] = useState(false);
  const [railwayAreas, setRailwayAreas] = useState<RailwayArea[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchRailwayAreas = () => {
    setRailwayAreasLoading(true);
    Fetcher.get<RailwayAreaResponseArgs[]>('/admin/railway_areas.json')
      .then(res => {
        const areas = res.map(
          railwayAreaData => new RailwayArea(convertRailwayAreaResponseToArgs(railwayAreaData))
        );
        setRailwayAreas(areas);
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
      })
      .finally(() => {
        setRailwayAreasLoading(false);
      });
  };

  const saveRailwayArea = (area: RailwayArea) => {
    Fetcher.put(`/admin/railway_areas/${area.id}.json`, area.submitParams)
      .then(() => {
        setSuccessMessage('保存しました。');
        setErrorMessage(null);
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
        setSuccessMessage(null);
      });
  };

  const updateRailwayAreaTicketingService = (areaId: number, ticketingQrService: number) => {
    // MobXを使って直接更新
    const area = railwayAreas.find(a => a.id === areaId);
    if (area) {
      area.ticketingQrService = ticketingQrService;
      saveRailwayArea(area);
    }
  };

  useEffect(() => {
    fetchRailwayAreas();
  }, []);

  return (
    <>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} style={{ marginBottom: '16px' }}>
          {successMessage}
        </Alert>
      )}
      {railwayAreasLoading ? (
        <Loading />
      ) : (
        <TableContainer>
          <Table style={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>エリア名</TableCell>
                <TableCell>サービスID</TableCell>
                <TableCell>発券サービス設定</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {railwayAreas.map(area => (
                <TableRow key={area.id}>
                  <TableCell>{area.area}</TableCell>
                  <TableCell>{area.serviceId}</TableCell>
                  <TableCell>
                    <RadioGroup
                      value={area.ticketingQrService.toString()}
                      onChange={(_, value) => updateRailwayAreaTicketingService(area.id, Number(value))}
                      layout="horizontal"
                    >
                      <Radio value="0">えきねっと</Radio>
                      <Radio value="1">e5489</Radio>
                    </RadioGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default observer(RailwayAreas);
